import React, { useState } from 'react';
import { BookOpen, Lock, ChevronRight } from 'lucide-react';
import { developmentLaws } from '../data/developmentLaws';
import { useSubscription } from '../hooks/useSubscription';
import { UpgradeModal } from '../components/UpgradeModal';

const FrameworkDetailsPage = () => {
  const { limits, isFree, loading } = useSubscription();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedLaw, setSelectedLaw] = useState(null);

  // Convert object to array
  const lawsList = Object.entries(developmentLaws).map(([key, value]) => ({
    id: key,
    ...value
  }));

  const handleLawClick = (index, law) => {
    // Check limit dynamically
    const limit = limits?.lawsAccessCount || 10;
    if (index >= limit) {
      setIsUpgradeModalOpen(true);
      return;
    }
    setSelectedLaw(law);
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  const accessLimit = limits?.lawsAccessCount || 10;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <BookOpen className="text-indigo-600" />
          40の発達法則
        </h1>
        <p className="text-gray-600">
          人間の成長を加速させる普遍的な40の法則。
          {accessLimit < 40 && <span className="text-amber-600 ml-2 font-medium">({isFree ? 'Free' : 'Student'}プラン: 最初の{accessLimit}個のみ閲覧可能)</span>}
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {lawsList.map((law, index) => {
          const isLocked = index >= accessLimit;

          return (
            <div
              key={law.id}
              onClick={() => handleLawClick(index, law)}
              className={`
                relative p-5 rounded-xl border transition-all cursor-pointer
                ${isLocked
                  ? 'bg-gray-50 border-gray-200 opacity-75 hover:bg-gray-100'
                  : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-md'
                }
              `}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-xs font-bold text-indigo-500 mb-1 uppercase tracking-wider">
                    LAW {index + 1}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {law.name}
                  </h3>
                  <p className="text-gray-500 text-sm line-clamp-2">
                    {isLocked ? 'この法則を閲覧するにはアップグレードが必要です。' : law.description}
                  </p>
                </div>

                {isLocked ? (
                  <div className="bg-gray-200 p-2 rounded-full">
                    <Lock className="w-4 h-4 text-gray-500" />
                  </div>
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-300" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected Law Details Modal (Simple implementation) */}
      {selectedLaw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setSelectedLaw(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-8 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-4 text-indigo-900">{selectedLaw.name}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{selectedLaw.description}</p>
            <button
              onClick={() => setSelectedLaw(null)}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        featureName="40の発達法則（全閲覧）"
        recommendedPlan="standard"
      />
    </div>
  );
};

export default FrameworkDetailsPage;